// src/pages/AutomationSettingsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ToggleSwitch from '../components/ToggleSwitch';
import { LuClock, LuZap, LuFilter, LuArrowLeft } from 'react-icons/lu';
import apiClient from '../api/apiClient';

const AutomationSettingsPage = () => {
    const navigate = useNavigate();

    // State for Trading Hours
    const [isTradingHoursEnabled, setIsTradingHoursEnabled] = useState(true);
    const [startTime, setStartTime] = useState('09:30');
    const [endTime, setEndTime] = useState('14:00');

    // State for Auto-Square Off
    const [isAutoSquareOffEnabled, setIsAutoSquareOffEnabled] = useState(true);
    const [squareOffTime, setSquareOffTime] = useState('15:15');

    // State for Signal Filtering
    const [signalFilters, setSignalFilters] = useState({});

    // Available instruments to filter - will be fetched from API
    const [availableInstruments, setAvailableInstruments] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Fetch instruments and settings on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [instrumentsResponse, settingsResponse] = await Promise.all([
                    apiClient.get('/instruments'),
                    apiClient.get('/settings')
                ]);

                const instruments = instrumentsResponse.data;
                setAvailableInstruments(instruments);

                const settings = settingsResponse.data;
                setIsTradingHoursEnabled(settings.trading_hours_enabled);
                setStartTime(settings.trading_start_time);
                setEndTime(settings.trading_end_time);
                setIsAutoSquareOffEnabled(settings.auto_square_off_enabled);
                setSquareOffTime(settings.square_off_time);

                // Initialize signal_filters with defaults for all instruments
                const filters = {};
                instruments.forEach(instrument => {
                    filters[instrument] = settings.signal_filters ? settings.signal_filters[instrument] || false : true;
                });
                setSignalFilters(filters);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleFilterChange = (instrument) => {
        setSignalFilters(prevFilters => ({
            ...prevFilters,
            [instrument]: !prevFilters[instrument],
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = {
                trading_hours_enabled: isTradingHoursEnabled,
                trading_start_time: startTime,
                trading_end_time: endTime,
                auto_square_off_enabled: isAutoSquareOffEnabled,
                square_off_time: squareOffTime,
                signal_filters: signalFilters,
            };
            await apiClient.put('/settings', data);
            alert("Automation settings saved!");
            navigate('/settings'); // Go back to the main settings page
        } catch (error) {
            console.error('Error saving settings:', error);
            alert("Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className="bg-slate-50 min-h-screen p-4 md:p-6 space-y-6 pb-24">

            {/* Page Header with Back Button */}
            <div className="flex items-center space-x-3">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
                    <LuArrowLeft size={24} className="text-slate-700" />
                </button>
                <h1 className="text-3xl font-bold text-slate-800">Automation Rules</h1>
            </div>

            {/* Trading Hours Card */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                        <LuClock size={20} className="text-blue-600" />
                        <h3 className="text-lg font-bold text-slate-800">Trading Hours</h3>
                    </div>
                    <ToggleSwitch enabled={isTradingHoursEnabled} setEnabled={setIsTradingHoursEnabled} />
                </div>
                <p className="text-sm text-slate-500 mb-4">Only accept signals between the specified start and end times.</p>
                <div className={`grid grid-cols-2 gap-4 transition-opacity ${isTradingHoursEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <div>
                        <label className="text-sm font-semibold text-slate-600 mb-1 block">Start Time</label>
                        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="w-full p-2 bg-slate-100 border border-slate-300 rounded-lg"/>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-slate-600 mb-1 block">End Time</label>
                        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="w-full p-2 bg-slate-100 border border-slate-300 rounded-lg"/>
                    </div>
                </div>
            </div>

            {/* Auto-Square Off Card */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                        <LuZap size={20} className="text-red-600" />
                        <h3 className="text-lg font-bold text-slate-800">Auto-Square Off</h3>
                    </div>
                    <ToggleSwitch enabled={isAutoSquareOffEnabled} setEnabled={setIsAutoSquareOffEnabled} />
                </div>
                <p className="text-sm text-slate-500 mb-4">Automatically close all open positions at a specific time.</p>
                <div className={`transition-opacity ${isAutoSquareOffEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <label className="text-sm font-semibold text-slate-600 mb-1 block">Square-off Time</label>
                    <input type="time" value={squareOffTime} onChange={e => setSquareOffTime(e.target.value)} className="w-full md:w-1/2 p-2 bg-slate-100 border border-slate-300 rounded-lg"/>
                </div>
            </div>

            {/* Signal Filtering Card */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200">
                <div className="flex items-center space-x-3 mb-4">
                    <LuFilter size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-bold text-slate-800">Signal Filtering</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Enable or disable trading signals for specific instruments.</p>
                <div className="space-y-3">
                    {availableInstruments.map(instrument => (
                        <div key={instrument} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <span className="font-semibold text-slate-700">{instrument}</span>
                            <ToggleSwitch enabled={signalFilters[instrument] || false} setEnabled={() => handleFilterChange(instrument)} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Save Button */}
            <div className="pt-4">
                <button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </div>
    );
};

export default AutomationSettingsPage;
